import { useMemo } from 'react';

import { Accessibility, Car } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { GatingBanner } from '@/modulos/portal-credenciado/comum/GatingBanner';
import {
  ESTADO_DO_CADASTRO,
  OPCOES_DE_ESTADO_DO_CADASTRO,
  estadoDoCadastroValido,
} from '@/modulos/portal-credenciado/comum/recursos';
import { ErroApi } from '@/nucleo/auth/cliente-api';
import { mascaraPlaca } from '@/nucleo/formato/documentos';
import { useUrlFilters } from '@/nucleo/ganchos/useUrlFilters';
import { DataTable } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { PageHeader } from '@/ui/PageHeader';
import { StatusBadge } from '@/ui/StatusBadge';
import { Badge } from '@/ui/base/badge';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';

import { useVeiculos } from './dados';
import type { FiltroDeVeiculos, Veiculo } from './contrato';

/** As chaves da URL são as do protótipo — o mesmo link filtra igual nos dois. */
const PADROES = { search: '', status: '' };

/**
 * Módulo, e não dentro da tela: `useTableViewPreferences` pede lista estável.
 * A placa identifica a linha e não se oculta; o Status fica preso no fim do
 * bloco de dados e explica o filtro de mesmo nome.
 */
const COLUNAS: TableViewColumn<Veiculo>[] = [
  {
    id: 'placa',
    header: 'CRMC / Placa',
    // Só a placa: a API não devolve o CRM, e "CRM pendente" afirmaria o que ninguém apurou.
    cell: (veiculo) => (
      <span className="font-mono text-xs font-semibold">{mascaraPlaca(veiculo.placa)}</span>
    ),
    sortKey: 'placa',
    isCritical: true,
  },
  {
    id: 'marca_modelo',
    header: 'Marca/Modelo',
    cell: (veiculo) => <span className="text-sm">{veiculo.marca_modelo ?? '—'}</span>,
    sortKey: 'marca_modelo',
  },
  {
    id: 'tipo_veiculo',
    header: 'Tipo',
    // O tipo sai da capacidade TEG, que a API ainda não devolve.
    cell: () => <span className="text-sm text-muted-foreground">—</span>,
  },
  {
    id: 'capacidade_teg',
    header: 'Cap. TEG',
    cell: (veiculo) => <span className="text-sm font-medium">{veiculo.capacidade_teg ?? '—'}</span>,
    sortKey: 'capacidade_teg',
  },
  {
    id: 'info',
    header: 'Info',
    cell: (veiculo) => (
      <div className="flex items-center gap-1">
        {veiculo.acessivel === true && (
          <Badge variant="outline" className="gap-1 text-xs">
            <Accessibility className="h-3 w-3" aria-hidden="true" /> PCD
          </Badge>
        )}
      </div>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: (veiculo) => (
      <StatusBadge
        status={ESTADO_DO_CADASTRO[veiculo.situacao].selo}
        label={ESTADO_DO_CADASTRO[veiculo.situacao].rotulo}
      />
    ),
    sortKey: 'situacao',
    isStatusColumn: true,
    linkedFilterKeys: ['status'],
  },
];

/**
 * `/credenciado/veiculos` — "Meus Veículos", a frota do credenciado do lado do
 * portal, com a composição de `PortalVeiculos.tsx` do protótipo: as mesmas
 * seis colunas, a ordem por placa e a linha inteira abrindo o detalhe.
 *
 * O dado vem da API (`comum/recursos.ts`), que ainda devolve pouco: placa,
 * marca e modelo juntos, o estado do cadastro e os documentos. Por isso:
 * - CRM, tipo e capacidade TEG mostram "—" (o tipo sai da capacidade);
 * - "Status" é o estado do cadastro (em análise, aprovado, reprovado), o
 *   único estado que a API devolve;
 * - o filtro "Documentos" do protótipo, por vencimento, não veio: sem a
 *   validade dos documentos ele esvaziaria a lista em qualquer opção,
 *   afirmando que nada vence.
 *
 * Sem escrita: é tela de consulta.
 *
 * `embutida`: a tela entra como aba de Frota & Equipe, que já tem o `h1` da
 * página; o título desta vira `h2` (`PageHeader nivel={2}`). A rota própria
 * não passa a prop e continua com o `h1`.
 */
export function VeiculosPage({ embutida = false }: { embutida?: boolean }) {
  const navigate = useNavigate();
  const { values, setFilter, setMany, page, setPage, perPage, setPerPage } = useUrlFilters(PADROES);

  const filtro = useMemo<FiltroDeVeiculos>(
    () => ({
      busca: values.search || undefined,
      situacao: estadoDoCadastroValido(values.status),
    }),
    [values.search, values.status],
  );

  const { data, error, isLoading, isError, refetch } = useVeiculos(filtro, page, perPage);

  const temFiltro = Boolean(values.search || values.status);

  const campos: FilterFieldConfig[] = [
    {
      key: 'search',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por placa, marca ou modelo...',
      ariaLabel: 'Buscar veículos',
      flex: true,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [...OPCOES_DE_ESTADO_DO_CADASTRO],
      width: 'w-full sm:w-40',
    },
  ];

  const filterValues = useMemo(() => ({ status: values.status }), [values.status]);

  const tableView = useTableViewPreferences({
    contextKey: 'credenciado:veiculos:list',
    columns: COLUNAS,
    filterValues,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        nivel={embutida ? 2 : 1}
        title="Meus Veículos"
        description="Veículos vinculados ao seu credenciamento"
      />

      <GatingBanner />

      <FilterBar
        fields={campos}
        values={values}
        onChange={setFilter}
        // Limpa só os filtros desta tela: dentro de "Frota & Equipe" a URL também guarda a aba.
        onClear={() => setMany(PADROES)}
        defaultValues={PADROES}
      />

      {error instanceof ErroApi && error.pedeOutroCaminho ? (
        // 403: perfil sem acesso ou cadastro sem vínculo. Tentar de novo não resolve.
        <ErrorState variant="forbidden" description={error.message} />
      ) : isError ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível buscar os veículos agora. Tente novamente em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : (
        <DataTable
          columns={tableView.orderedColumns}
          personalizacao={tableView}
          data={data?.itens ?? []}
          isLoading={isLoading}
          total={data?.total}
          page={page}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          onRowClick={(veiculo) => navigate(`/credenciado/veiculos/${veiculo.id}`)}
          emptyIcon={Car}
          emptyTitle={temFiltro ? 'Nenhum veículo encontrado' : 'Nenhum veículo cadastrado'}
          emptyDescription={
            temFiltro
              ? 'Nenhum veículo atende a esses filtros. Tente afrouxar a busca.'
              : 'Não há veículos vinculados ao seu credenciamento.'
          }
          minWidth="700px"
          ariaLabel="Lista de veículos do credenciado"
        />
      )}
    </div>
  );
}

export default VeiculosPage;
